import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FileWarning, FileText, AlertCircle, Plus, Trash2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ContradictionsBlock as ContradictionsBlockType } from "@/canvas/types/blocks";
import type { Contradiction } from "@/canvas/types/content";
import { cn } from "@/lib/utils";
import { useCanvasStore } from "../../store";
import { generateItemId } from "../../utils/id";
import {
  AnalysisListCard,
  AnalysisListCardDragHandle,
  AnalysisListAddButton,
  AnalysisListEditableTitle,
  AnalysisListEditableHeaderTitle,
  AnalysisListHeader,
  AnalysisListMetric,
  DocumentReferenceChips,
  replaceAnalysisListItems,
} from "./AnalysisList";
import { DocumentCitationChip } from "./CitationChip";

const markdownComponents: Components = {
  p: ({ node: _node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
  strong: ({ node: _node, ...props }) => (
    <strong className="font-semibold text-foreground" {...props} />
  ),
  em: ({ node: _node, ...props }) => <em className="italic" {...props} />,
  ul: ({ node: _node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
  ol: ({ node: _node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
  li: ({ node: _node, ...props }) => <li {...props} />,
  a: ({ href, children, node: _node }) => {
    if (href?.startsWith("ref:")) {
      const withoutScheme = href.slice(4);
      const [uuidPart, hashPart] = withoutScheme.split("#");
      const sourceFileId = uuidPart || undefined;
      const parsedPage = hashPart?.startsWith("page=") ? parseInt(hashPart.slice(5), 10) : NaN;
      const pageNumber = Number.isNaN(parsedPage) ? undefined : parsedPage;
      return (
        <DocumentCitationChip
          fileName={typeof children === "string" ? children : String(children ?? "")}
          sourceFileId={sourceFileId}
          pageNumber={pageNumber}
        />
      );
    }

    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

interface ContradictionsBlockProps {
  readOnly?: boolean;
  block: ContradictionsBlockType;
}

function getDocumentName(ref: string | { fileName: string; pageNumber?: number }): string {
  if (typeof ref === "string") return ref;
  return ref.fileName;
}

interface ContradictionCardProps {
  contradiction: Contradiction;
  readOnly: boolean;
  onUpdateContradiction: (updated: Contradiction) => void;
  onRemoveContradiction: (id: string) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

function ContradictionCard({
  contradiction,
  readOnly,
  onUpdateContradiction,
  onRemoveContradiction,
  dragHandleProps,
}: ContradictionCardProps) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [localTitle, setLocalTitle] = useState(contradiction.title);
  const [localDescription, setLocalDescription] = useState(contradiction.description || "");

  useEffect(() => {
    if (!isEditingTitle) setLocalTitle(contradiction.title);
  }, [contradiction.title, isEditingTitle]);

  useEffect(() => {
    if (!isEditingDescription) setLocalDescription(contradiction.description || "");
  }, [contradiction.description, isEditingDescription]);

  useEffect(() => {
    if (isOpen && cardRef.current) {
      const timeoutId = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  const hasDocuments = (contradiction.documentRefs?.length ?? 0) > 0;
  const hasDetails = Boolean(contradiction.description || hasDocuments || !readOnly);

  const handleStartEditingTitle = useCallback(() => {
    setLocalTitle(contradiction.title);
    setIsEditingTitle(true);
  }, [contradiction.title]);

  const handleStartEditingDescription = useCallback(() => {
    setLocalDescription(contradiction.description || "");
    setIsEditingDescription(true);
  }, [contradiction.description]);

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    const trimmed = localTitle.trim();
    if (!trimmed) {
      setLocalTitle(contradiction.title);
    } else if (trimmed !== contradiction.title) {
      onUpdateContradiction({ ...contradiction, title: trimmed });
    }
  }, [contradiction, localTitle, onUpdateContradiction]);

  const handleDescriptionBlur = useCallback(() => {
    setIsEditingDescription(false);
    const newDescription = localDescription.trim() || undefined;
    if (newDescription !== contradiction.description) {
      onUpdateContradiction({ ...contradiction, description: newDescription });
    }
  }, [contradiction, localDescription, onUpdateContradiction]);

  return (
    <AnalysisListCard
      open={isOpen}
      onOpenChange={setIsOpen}
      expandable={hasDetails}
      cardRef={cardRef}
      dragHandle={
        !readOnly && dragHandleProps ? (
          <AnalysisListCardDragHandle {...dragHandleProps}>
            <GripVertical className="h-4 w-4" />
          </AnalysisListCardDragHandle>
        ) : undefined
      }
      leading={
        <div className="shrink-0 text-warning">
          <AlertCircle className="h-4 w-4" />
        </div>
      }
      title={
        <div className="min-w-0">
          <AnalysisListEditableTitle
            value={contradiction.title}
            draftValue={localTitle}
            isEditing={isEditingTitle}
            isOpen={isOpen}
            readOnly={readOnly}
            onChange={setLocalTitle}
            onStartEditing={handleStartEditingTitle}
            onCommit={handleTitleBlur}
            onCancel={() => {
              setLocalTitle(contradiction.title);
              setIsEditingTitle(false);
            }}
            displayClassName="truncate"
          />
        </div>
      }
      trailing={
        hasDocuments ? (
          <div className="flex items-center gap-1">
            <FileText className="h-3.5 w-3.5 text-foreground/50" />
            <span className="text-ui-body-xs text-foreground/60 tabular-nums">
              {contradiction.documentRefs?.length ?? 0}
            </span>
          </div>
        ) : undefined
      }
      body={
        isEditingDescription && !readOnly ? (
          <textarea
            value={localDescription}
            onChange={(event) => setLocalDescription(event.target.value)}
            onBlur={handleDescriptionBlur}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setLocalDescription(contradiction.description || "");
                setIsEditingDescription(false);
              }
              event.stopPropagation();
            }}
            onClick={(event) => event.stopPropagation()}
            placeholder={t("canvas.blocks.contradictions.addDescription", "Add description...")}
            className="text-ui-body-sm text-foreground/80 leading-relaxed bg-transparent border-0 rounded px-1 -mx-1 py-0.5 resize-none focus-visible:ring-0 outline-none min-h-[1.5em] w-full block"
            style={{ fieldSizing: "content" } as React.CSSProperties}
            autoFocus
          />
        ) : contradiction.description ? (
          <div
            className={cn(
              "text-ui-body-sm max-w-none text-foreground/80",
              !readOnly &&
                "cursor-text hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors",
            )}
            onClick={() => {
              if (!readOnly) handleStartEditingDescription();
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              urlTransform={(url) => (url.startsWith("ref:") ? url : defaultUrlTransform(url))}
              components={markdownComponents}
            >
              {contradiction.description}
            </ReactMarkdown>
          </div>
        ) : !readOnly ? (
          <p
            className="text-ui-body-sm text-foreground/50 italic cursor-text hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors"
            onClick={() => handleStartEditingDescription()}
          >
            {t("canvas.blocks.contradictions.addDescription", "Add description...")}
          </p>
        ) : null
      }
      footer={
        <>
          <div className="flex-1">
            <DocumentReferenceChips refs={contradiction.documentRefs} />
          </div>
          {!readOnly ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0 ml-4"
              onClick={(event) => {
                event.stopPropagation();
                onRemoveContradiction(contradiction.id);
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              {t("common.delete", "Delete")}
            </Button>
          ) : null}
        </>
      }
    />
  );
}

interface SortableContradictionCardProps extends Omit<ContradictionCardProps, "dragHandleProps"> {
  id: string;
}

function SortableContradictionCard({ id, ...props }: SortableContradictionCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ContradictionCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

export function ContradictionsBlock({ block, readOnly = false }: ContradictionsBlockProps) {
  const { t } = useTranslation();
  const defaultHeaderText = t("canvas.blocks.contradictions.headers.contradictions");
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [localHeader, setLocalHeader] = useState(block.title || defaultHeaderText);
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  const updateBlock = useCanvasStore((state) => state.updateBlock);

  useEffect(() => {
    if (!isEditingHeader) setLocalHeader(block.title || defaultHeaderText);
  }, [block.title, defaultHeaderText, isEditingHeader]);

  const contradictions = useMemo<Contradiction[]>(() => {
    return (block.content.items as Contradiction[] | undefined) ?? [];
  }, [block.content.items]);

  const headerText = block.title || defaultHeaderText;

  const handleStartEditingHeader = useCallback(() => {
    setLocalHeader(block.title || defaultHeaderText);
    setIsEditingHeader(true);
  }, [block.title, defaultHeaderText]);

  const handleHeaderBlur = useCallback(() => {
    setIsEditingHeader(false);
    const trimmedTitle = localHeader.trim();
    const newTitle = trimmedTitle && trimmedTitle !== defaultHeaderText ? trimmedTitle : undefined;
    if (newTitle !== block.title) {
      updateBlock(block.id, { title: newTitle });
    }
  }, [block.id, block.title, defaultHeaderText, localHeader, updateBlock]);

  const setContradictionItems = useCallback(
    (items: Contradiction[]) => {
      updateBlockContent(
        block.id,
        replaceAnalysisListItems(block.content, items) as Parameters<typeof updateBlockContent>[1],
      );
    },
    [block.content, block.id, updateBlockContent],
  );

  const handleUpdateContradiction = useCallback(
    (updatedContradiction: Contradiction) => {
      setContradictionItems(
        contradictions.map((contradiction) =>
          contradiction.id === updatedContradiction.id ? updatedContradiction : contradiction,
        ),
      );
    },
    [contradictions, setContradictionItems],
  );

  const handleAddContradiction = useCallback(() => {
    const newContradiction: Contradiction = {
      id: generateItemId(),
      title: t("canvas.blocks.contradictions.newTitle", "New contradiction"),
      description: undefined,
    };
    setContradictionItems([...contradictions, newContradiction]);
  }, [contradictions, setContradictionItems, t]);

  const handleRemoveContradiction = useCallback(
    (contradictionId: string) => {
      setContradictionItems(
        contradictions.filter((contradiction) => contradiction.id !== contradictionId),
      );
    },
    [contradictions, setContradictionItems],
  );

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = contradictions.findIndex((contradiction) => contradiction.id === active.id);
      const newIndex = contradictions.findIndex((contradiction) => contradiction.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const nextContradictions = [...contradictions];
      const [movedContradiction] = nextContradictions.splice(oldIndex, 1);
      nextContradictions.splice(newIndex, 0, movedContradiction);
      setContradictionItems(nextContradictions);
    },
    [contradictions, setContradictionItems],
  );

  const uniqueDocuments = useMemo(() => {
    const documents = new Set<string>();
    contradictions.forEach((contradiction) => {
      (contradiction.documentRefs ?? []).forEach((ref) => {
        documents.add(getDocumentName(ref));
      });
    });
    return documents;
  }, [contradictions]);

  if (contradictions.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-muted-foreground/20 p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted/50 mb-3">
          <FileWarning className="h-6 w-6 text-muted-foreground/50" />
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          {t("canvas.blocks.contradictions.empty")}
        </p>
        {!readOnly ? (
          <Button variant="outline" size="sm" onClick={handleAddContradiction}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t("canvas.blocks.contradictions.add", "Add contradiction")}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AnalysisListHeader
        title={
          <AnalysisListEditableHeaderTitle
            value={headerText}
            draftValue={localHeader}
            isEditing={isEditingHeader}
            readOnly={readOnly}
            onChange={setLocalHeader}
            onStartEditing={handleStartEditingHeader}
            onCommit={handleHeaderBlur}
            onCancel={() => {
              setLocalHeader(block.title || defaultHeaderText);
              setIsEditingHeader(false);
            }}
          />
        }
        metrics={
          <AnalysisListMetric
            icon={<AlertCircle className="h-4 w-4 text-warning" />}
            value={contradictions.length}
            tooltip={
              <p>
                {contradictions.length}{" "}
                {contradictions.length === 1
                  ? t("canvas.blocks.contradictions.singular", "contradiction")
                  : t("canvas.blocks.contradictions.plural", "contradictions")}
              </p>
            }
          />
        }
        trailing={
          uniqueDocuments.size > 0 ? (
            <AnalysisListMetric
              icon={<FileText className="h-4 w-4 text-muted-foreground" />}
              value={uniqueDocuments.size}
              valueClassName="font-mono"
              className="text-sm text-muted-foreground"
              tooltip={
                <p>
                  {t("canvas.blocks.contradictions.documentsInvolved", "Documents involved")}:{" "}
                  {uniqueDocuments.size}
                </p>
              }
            />
          ) : undefined
        }
      />

      {readOnly ? (
        <div className="flex flex-col gap-2">
          {contradictions.map((contradiction) => (
            <ContradictionCard
              key={contradiction.id}
              contradiction={contradiction}
              readOnly={readOnly}
              onUpdateContradiction={handleUpdateContradiction}
              onRemoveContradiction={handleRemoveContradiction}
            />
          ))}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={contradictions.map((contradiction) => contradiction.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-2">
              {contradictions.map((contradiction) => (
                <SortableContradictionCard
                  key={contradiction.id}
                  id={contradiction.id}
                  contradiction={contradiction}
                  readOnly={readOnly}
                  onUpdateContradiction={handleUpdateContradiction}
                  onRemoveContradiction={handleRemoveContradiction}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {!readOnly ? (
        <AnalysisListAddButton onClick={handleAddContradiction}>
          <Plus className="h-4 w-4" />
          {t("canvas.blocks.contradictions.add", "Add contradiction")}
        </AnalysisListAddButton>
      ) : null}
    </div>
  );
}
